import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: boolean | null;
  userData: UserData | null;
  refetch: () => void;
}

interface UserData {
    emailAddress: string;
    lastIn: string;
    name: string;
    roles: string[];
}

export const useAuth = (): AuthState => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress, instance  } = useMsal();
  const [hasPermission, setHasPermission] = useState(null as boolean | null);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null as UserData | null);

  const loadUser = useCallback(() => {
    if (isAuthenticated === false) {
      setHasPermission(false);
      setIsLoading(false);
      setUserData(null);
      localStorage.removeItem('userData');
      return;
    }
    if (accounts.length === 0) {
      setIsLoading(true);
      return;
    }

    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      console.error('useAuth - Token fetch timeout - took too long');
      setHasPermission(false);
      setIsLoading(false);
    }, 10000);

    try {
      // Get roles from MSAL ID token
      const account = accounts[0];
      const idTokenClaims = account.idTokenClaims as any;
      
      // Extract roles from token claims
      const roles = idTokenClaims?.roles || [];
      
      // Check if user has required roles/permissions
      const hasRequiredPermission = roles.length > 0; // Adjust this logic based on your requirements
      
      
      if (hasRequiredPermission) {
        const userData: UserData = {
          emailAddress: idTokenClaims?.preferred_username || idTokenClaims?.email || account.username,
          name: idTokenClaims?.name || '',
          lastIn: new Date().toISOString(),
          roles,
        };
        
        setHasPermission(true);
        setUserData(userData);
        localStorage.setItem('userData', JSON.stringify(userData));
      } else {
        console.warn('useAuth - User has no required roles', roles);
        setHasPermission(false);
        setUserData(null);
        localStorage.removeItem('userData');
      }
      
      clearTimeout(timeoutId);
      setIsLoading(false);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('useAuth - Error processing token', error);
      setHasPermission(false);
      setIsLoading(false);
    }
  }, [accounts, isAuthenticated, instance]);

  useEffect(() => {
    if (inProgress === 'none') {
      loadUser();
    }
  }, [loadUser, inProgress]);
 
  return {
    isLoading,
    isAuthenticated: isAuthenticated && hasPermission === true,
    hasPermission,
    userData,
    refetch: loadUser,
  };
};
